import { createContext, useContext, useState, ReactNode } from "react";
import { ToastContainer } from "./Toast";

export type ToastType = "success" | "error" | "info" | "warning";

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  description?: string;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (
    type: ToastType,
    message: string,
    description?: string,
    duration?: number
  ) => void;
  removeToast: (id: string) => void;
  success: (message: string, description?: string, duration?: number) => void;
  error: (message: string, description?: string, duration?: number) => void;
  info: (message: string, description?: string, duration?: number) => void;
  warning: (message: string, description?: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = (
    type: ToastType,
    message: string,
    description?: string,
    duration = 5000
  ) => {
    const id = Math.random().toString(36).substring(2, 11);
    setToasts((prev) => [...prev, { id, type, message, description, duration }]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const value: ToastContextValue = {
    toasts,
    addToast,
    removeToast,
    success: (message: string, description?: string, duration?: number) =>
      addToast("success", message, description, duration),
    error: (message: string, description?: string, duration?: number) =>
      addToast("error", message, description, duration),
    info: (message: string, description?: string, duration?: number) =>
      addToast("info", message, description, duration),
    warning: (message: string, description?: string, duration?: number) =>
      addToast("warning", message, description, duration),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
}
